import {
	getAllInvestorsValuations,
	getInvestorValuations,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { GetInvestorValuationsRes, GetValuationsRes } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { HYPHEN_MINUS } from '@dealstack/frontend/shared/other/react/specialSigns';
import { Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

const HEADER_TITLE_ID = {
	PROGRAM: 'program',
	SECURITY: 'security',
	VOLUME: 'volume',
	COST: 'cost',
	VALUE: 'value',
};
export const useHoldingsTableController = (hasAllInvestmentsOption: boolean, enabled = true) => {
	const { investorId } = useParams();
	const tInvestor = useTranslationInvestor();

	const getSpecificInvestorValuationsRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SHAREHOLDER_INVESTMENT_VALUATIONS_INVESTORID_GET, {
				investorId: investorId ?? '',
			}),
		],
		queryFn: ({ signal }) =>
			getInvestorValuations(
				formatUnicorn(API_PATHS.SHAREHOLDER_INVESTMENT_VALUATIONS_INVESTORID_GET, {
					investorId: investorId ?? '',
				}),
				signal
			),
		enabled: enabled && !hasAllInvestmentsOption && !!investorId,
	});
	const getAllInvestorValuationsRequest = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_INVESTMENT_VALUATIONS_GET],
		queryFn: ({ signal }) =>
			getAllInvestorsValuations(API_PATHS.SHAREHOLDER_INVESTMENT_VALUATIONS_GET, signal),
		enabled: enabled && !!hasAllInvestmentsOption,
	});
	const specificValuationResponsePayload = getSpecificInvestorValuationsRequest.data?.payload;
	let specificValuationResponse: GetInvestorValuationsRes[] = [];
	if (specificValuationResponsePayload) {
		specificValuationResponse = [specificValuationResponsePayload];
	}
	const getAllInvestorValuationsResponse = getAllInvestorValuationsRequest.data?.payload ?? [];

	const holdingsDataColumns: ColumnDef<GetValuationsRes>[] = useMemo(() => {
		const data = hasAllInvestmentsOption
			? getAllInvestorValuationsResponse
			: specificValuationResponse;
		if (!data || !data?.length) {
			return [];
		}
		const columns = [
			{
				id: HEADER_TITLE_ID.PROGRAM,
				header: () => <Text size="B6Bold">{tInvestor('valuations.table.programme')}</Text>,

				cell: (info) => (
					<>
						<Text size="B6Bold">{info.row.original.programmeName}</Text>
						<Text size="B6Light"> {info.row.original.entityName}</Text>
					</>
				),
				size: 300,
			},
			{
				id: HEADER_TITLE_ID.SECURITY,
				header: () => <Text size="B6Bold">{tInvestor('valuations.table.security')}</Text>,
				cell: (info) => <Text size="B6Medium">{info.row.original.name}</Text>,
				size: 100,
			},
			{
				id: HEADER_TITLE_ID.VOLUME,
				header: () => (
					<Text size="B6Bold" style={{ textAlign: 'right' }}>
						{tInvestor('valuations.table.volume')}
					</Text>
				),
				cell: (info) => (
					<Text size="B6Medium" style={{ textAlign: 'right' }}>
						<AmountFormatted
							value={info.row.original.volume}
							currency={info.row.original.currency}
							showCurrency={false}
							decimalScale={0}
						/>
					</Text>
				),
				size: 50,
			},
			{
				id: HEADER_TITLE_ID.COST,
				header: () => (
					<Text size="B6Bold" style={{ textAlign: 'right' }}>
						{tInvestor('valuations.table.cost')}
					</Text>
				),
				cell: (info) => (
					<Text size="B6Medium" style={{ textAlign: 'right' }}>
						<AmountFormatted
							value={info.row.original.cost}
							currency={info.row.original.currency}
							showNegative
						/>
					</Text>
				),
				size: 50,
			},
		];

		// eslint-disable-next-line eqeqeq
		if (data.some((valuation) => valuation?.valuations.some((val) => val.value != undefined))) {
			columns.push({
				id: HEADER_TITLE_ID.VALUE,
				header: () => (
					<Text size="B6Bold" style={{ textAlign: 'right' }}>
						{`${tInvestor('valuations.table.value')}
                        (${
							formatStringDateToUI(
								data[0].securityValuationDate,
								DATE_FORMATS.CALENDAR_DATE
							) ?? HYPHEN_MINUS
						})`}
					</Text>
				),
				cell: (info) => (
					<Text size="B6Bold" style={{ textAlign: 'right' }}>
						<AmountFormatted
							value={info.row.original.value}
							currency={info.row.original.currency}
							showNegative
						/>
					</Text>
				),
				size: 50,
			});
		}

		return columns;
	}, [specificValuationResponse, getAllInvestorValuationsResponse]);

	const holdingsData = hasAllInvestmentsOption
		? getAllInvestorValuationsResponse
		: specificValuationResponse;

	const showValuationsAlert = useMemo(
		() => holdingsData.some((holding) => holding.shareWithInvestorSecurityValuation),
		[holdingsData]
	);

	return {
		holdingsData,
		holdingsDataColumns,
		showValuationsAlert,
		isLoading: hasAllInvestmentsOption
			? getAllInvestorValuationsRequest.isLoading
			: getSpecificInvestorValuationsRequest.isLoading,
	};
};
